import { invalidateDeviceStateSchema } from '@/lib/queryClient'
import type { DeviceStatusExpression } from '@/types/deviceStatus'
import { asList } from '@/utils/asList'
import { apiCall } from './axiosInstance'

/**
 * A derived device state: a named device_state column whose value is the first of `expressions`
 * whose condition a device matches.
 *
 * `expressions` is opaque JSONB to the backend — it neither validates nor evaluates it today, so
 * the icon and color the UI renders each state with travel inside it.
 */
export interface DerivedDeviceState {
	id: number
	name: string
	expressions: DeviceStatusExpression
}

export type DerivedDeviceStateParams = Omit<DerivedDeviceState, 'id'>

export interface DerivedDeviceStateCreatedResponse {
	id: number
}

export interface DerivedDeviceStateStatusResponse {
	status: string
}

const tenantHeaders = (tenantId?: string, impersonateAdmin = true) =>
	tenantId
		? {
				headers: {
					'x-bytebeam-tenant': tenantId,
					...(impersonateAdmin ? { 'x-bytebeam-impersonate': 'admin' } : {}),
				},
			}
		: undefined

export const derivedDeviceStateApi = {
	getDerivedDeviceStates: async (tenantId?: string, impersonateAdmin = true): Promise<DerivedDeviceState[]> => {
		const states = await apiCall<DerivedDeviceState[]>(
			'/v3/derived-device-state',
			tenantHeaders(tenantId, impersonateAdmin),
		)
		return asList(states)
	},

	createDerivedDeviceState: async (
		params: DerivedDeviceStateParams,
		tenantId?: string,
	): Promise<DerivedDeviceStateCreatedResponse> => {
		const response = await apiCall<DerivedDeviceStateCreatedResponse>('/v3/derived-device-state', {
			method: 'POST',
			data: params,
			...tenantHeaders(tenantId),
		})
		void invalidateDeviceStateSchema()
		return response
	},

	updateDerivedDeviceState: async (
		id: number,
		params: DerivedDeviceStateParams,
		tenantId?: string,
	): Promise<DerivedDeviceStateStatusResponse> => {
		const response = await apiCall<DerivedDeviceStateStatusResponse>(`/v3/derived-device-state/${id}`, {
			method: 'PATCH',
			data: params,
			...tenantHeaders(tenantId),
		})
		void invalidateDeviceStateSchema()
		return response
	},

	deleteDerivedDeviceState: async (id: number, tenantId?: string): Promise<DerivedDeviceStateStatusResponse> => {
		const response = await apiCall<DerivedDeviceStateStatusResponse>(`/v3/derived-device-state/${id}`, {
			method: 'DELETE',
			...tenantHeaders(tenantId),
		})
		void invalidateDeviceStateSchema()
		return response
	},
}
